import {
  collection,
  getDocs,
  query,
  limit,
  orderBy,
  OrderByDirection,
  getCountFromServer,
  QueryDocumentSnapshot,
  DocumentData,
  startAfter,
  QueryConstraint,
  CollectionReference,
} from 'firebase/firestore';
import Failure from '../../../core/Failure';
import { FirestoreCollections, firestore } from '../../../firebase.config';
import { ICompany } from '@leedpulse-monorepo/interfaces';

export interface CompanyFilters {
  status?: CompanyStatus,
};

export type CompanyStatus = 'expired' | 'active';

async function getCompaniesWithFilters(
  filters: CompanyFilters,
  orderByName: string | undefined,
  sortDirection: OrderByDirection | undefined,
  offset: number,
  lastVisible?: QueryDocumentSnapshot<DocumentData, DocumentData>
): Promise<{
  companies: ICompany[];
  totalCount: number;
  lastVisible: QueryDocumentSnapshot<DocumentData, DocumentData>;
}> {
  try {
    const db = firestore;
    const companiesCollection = collection(
      db,
      FirestoreCollections.companies
    );

    const companies: ICompany[] = [];

    // Because of firestore limitation, we need to do several queries if scenario ids exceeds 30
    const results = await getCompanyDocsWithoutChunks(companiesCollection,
      [],
      orderByName,
      sortDirection,
      offset,
      lastVisible);

    results.docs.forEach((doc) => {
      companies.push(doc.data() as ICompany);
    });

    const companiesSnapshot = await getCountFromServer(
      query(
        companiesCollection
      )
    );

    return {
      companies,
      totalCount: companiesSnapshot.data().count ?? 0,
      lastVisible: results.docs[results.docs.length - 1]
    };
  } catch (e) {
    console.log('error: ', e);
    throw new GetCompaniesWithFiltersFailure();
  }
}

async function getCompanyDocsWithoutChunks(
  companiesCollection: CollectionReference,
  companyConstraints: QueryConstraint[],
  orderByName: string | undefined,
  sortDirection: OrderByDirection | undefined,
  offset: number,
  lastVisible?: QueryDocumentSnapshot<DocumentData, DocumentData>
) {
  const constraints: QueryConstraint[] = [
    ...companyConstraints,
    orderBy(
      orderByName ?? 'created',
      sortDirection ?? 'desc'
    ),
    limit(offset)
  ];

  if (lastVisible) {
    constraints.push(startAfter(lastVisible))
  }

  return await getDocs(
    query(
      companiesCollection,
      ...constraints
    )
  );
};

export class GetCompaniesWithFiltersFailure extends Failure {
  constructor(message = 'Unknown error', code = 'unknown-error') {
    super(message, code);
  }
}

export default getCompaniesWithFilters;
